import React, { useEffect, useRef, useState } from "react";

import { useMutation } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";

import { NotificationToast } from "../../../components/Generic/Toast/NotificationToast";
import {
  CURRENT_USER,
  CUSTOMER_PORTAL_PUBLIC_ROUTES,
  CUSTOMER_PORTAL_THEME,
  USER_TOKEN,
  USER_UID,
} from "../../../constants";
import { useAppState } from "../../../context/StateContext";
import { useSecretManager } from "../../../hooks/useSecretManager";
import { Error } from "../../../models/error";
import CustomerPortalService from "../../../services/customer.portal.service";
import { useCustomerPortalStore } from "../../../stores/customerPortal/customerPortalStore";
import { CustomerUserResponseData } from "../../../types";
import { checkTokenCustomerInvite } from "../../../util/checkTokenCustomerInvite";
import { CodeError } from "../../../util/enums";
import env from "../../../util/env";
import { getTokenCustomerPortalByWindowLocation } from "../../../util/getTokenCustomerPortalByWindowLocation";
import { setWindowLocationWithTokenCustomerPortal } from "../../../util/setWindowLocationWithTokenCustomerPortal";
import FormLogin, { LoginFormHandler } from "./Form";

interface CustomerPortalToken {
  companyId: number;
  customerId: number;
  jobId: number;
}

const CustomerPortalLogin = () => {
  const [errorReturned, setErrorReturned] = useState<Error>();

  const loginFormComponentRef = useRef<LoginFormHandler>(null);

  const { setCurrentCustomerUser, subDomainInfo } = useAppState();

  const setThemeFromDatabase = useCustomerPortalStore((state) => state.setThemeFromDatabase);

  const { t: translate } = useTranslation();

  const history = useHistory();

  const { secret } = useSecretManager(env.SECRET_ID);

  const [customerPortalToken, setCustomerPortalToken] = useState<CustomerPortalToken>();
  const [loadingCustomerPortalToken, setLoadingCustomerPortalToken] = useState<boolean>(false);

  useEffect(() => {
    const token = getTokenCustomerPortalByWindowLocation();

    if (secret?.SECRET_KEY_PASSWORD && token) {
      const customerPortalToken = checkTokenCustomerInvite<CustomerPortalToken>(
        token,
        secret?.SECRET_KEY_PASSWORD
      );

      if (customerPortalToken) setCustomerPortalToken(customerPortalToken);
    }
  }, [secret]);

  const { mutate: validateUser, isLoading: isLoadingValidate } = useMutation(
    ["portal.login"],
    (login: { email: string; password: string; companyId: number }) =>
      CustomerPortalService.login(login.email, login.password, login?.companyId),
    {
      onSuccess: async (response) => {
        if (response?.data?.success) {
          setLoadingCustomerPortalToken(true);
          await onFinishLogin(response?.data);
          setErrorReturned(undefined);
        }
      },
      onError: (err: AxiosError<Error>) => {
        if (err.response?.data?.error === CodeError.FORBIDDEN) {
          NotificationToast.error(err?.response?.data?.message);
        } else {
          const errorReturned = err?.response?.data ? (err.response?.data as Error) : undefined;

          setErrorReturned(errorReturned);
        }
      },
    }
  );

  const onNotFoundOrders = () => {
    setLoadingCustomerPortalToken(false);
    NotificationToast.error(translate("anUnknownErrorHasOccurred"));
    localStorage.removeItem(USER_UID);
    localStorage.removeItem(USER_TOKEN);
  };

  const onFinishLogin = async (customerUserResponseData?: CustomerUserResponseData) => {
    if (!customerUserResponseData) return;
    const { customerUser, token, customerPortalTheme } = customerUserResponseData;

    localStorage.setItem(USER_UID, token);
    localStorage.setItem(USER_TOKEN, token);

    if (customerPortalTheme) {
      localStorage.setItem(CUSTOMER_PORTAL_THEME, JSON.stringify(customerPortalTheme));
      setThemeFromDatabase(customerPortalTheme);
    }

    const finalizeCustomerSave = () => {
      setCurrentCustomerUser(customerUser);
      setLoadingCustomerPortalToken(false);
    };

    try {
      let lastOrderSelectedId: number | undefined = customerPortalToken?.jobId;

      NotificationToast.success(translate("userLoggedIn"));
      localStorage.setItem(CURRENT_USER, JSON.stringify(customerUser));

      if (!customerUser?.verified) {
        finalizeCustomerSave();

        history.replace(CUSTOMER_PORTAL_PUBLIC_ROUTES.EMAIL_CONFIRMATION);

        return;
      }

      if (!lastOrderSelectedId) {
        const firstOrder = await CustomerPortalService.getOrders().then(({ data }) => data.at(0));

        lastOrderSelectedId = firstOrder?.id;
      }

      finalizeCustomerSave();

      if (lastOrderSelectedId) {
        setWindowLocationWithTokenCustomerPortal(
          history,
          {
            companyId: customerUser?.companyId,
            jobId: lastOrderSelectedId,
            customerId: customerUser?.customerId,
          } as CustomerPortalToken,
          secret
        );

        return;
      }
    } catch (error) {
      onNotFoundOrders();
    }
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    if (subDomainInfo?.companyId) {
      validateUser({
        email: loginFormComponentRef?.current?.email ?? "",
        password: loginFormComponentRef?.current?.password ?? "",
        companyId: subDomainInfo?.companyId,
      });
    }
  };

  return (
    <FormLogin
      ref={loginFormComponentRef}
      isLoading={isLoadingValidate || loadingCustomerPortalToken}
      errorReturned={errorReturned}
      handleSubmit={handleSubmit}
    />
  );
};

export default CustomerPortalLogin;
